import { memo, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseDeviceModal from '~/components/common/base-device-modal/BaseDeviceModal';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import personApi from '~/apis/person';
import deviceApi from '~/apis/device';
import { handleApiError, notify } from '~/utils/helper';
import { IMasterDataItem } from '~/utils/interface/master';

type AssignDeviceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  personId: number;
  cccd: string;
  fullName: string;
  onSuccess?: () => void;
};

const useAssignDeviceApi = () => {
  const [isLoading, setIsLoading] = useState(false);

  const assignDevice = async (personId: number, imei: string) => {
    setIsLoading(true);
    try {
      await personApi.assignDevice(personId, { imei });
      return true;
    } catch (error) {
      handleApiError(error, true);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { assignDevice, isLoading };
};

const useUnassignedImeis = (isOpen: boolean) => {
  const [imeiOptions, setImeiOptions] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      setIsFetching(true);
      try {
        const response = await deviceApi.getUnassignedImeis({});
        setImeiOptions(response.data?.result ?? []);
      } catch (error) {
        handleApiError(error);
        setImeiOptions([]);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [isOpen]);

  return { imeiOptions, isFetching };
};

const AssignDeviceModal = ({ isOpen, onClose, personId, cccd, fullName, onSuccess }: AssignDeviceModalProps) => {
  const { t } = useTranslation('admin/common');
  const [imei, setImei] = useState('');
  const { assignDevice, isLoading } = useAssignDeviceApi();
  const { imeiOptions, isFetching } = useUnassignedImeis(isOpen);

  const selectOptions: IMasterDataItem[] = useMemo(
    () => imeiOptions.map((item) => ({ id: item, name: item })),
    [imeiOptions]
  );

  useEffect(() => {
    if (!isOpen) {
      setImei('');
    }
  }, [isOpen]);

  const handleClose = () => {
    setImei('');
    onClose();
  };

  const handleConfirm = async () => {
    const imeiValue = imei.trim();

    if (!imeiValue) {
      notify('error', t('deviceModal.selectDeviceWarning'));
      return;
    }

    const isSuccess = await assignDevice(personId, imeiValue);
    if (isSuccess) {
      notify('success', t('deviceModal.assignSuccess'));
      onSuccess?.();
      handleClose();
    }
  };

  const handleChangeImei = (event: any) => {
    setImei(String(event.target.value ?? ''));
  };

  return (
    <BaseDeviceModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('deviceModal.assignTitle')}
      cccd={cccd}
      fullName={fullName}
      onConfirm={handleConfirm}
      isConfirmLoading={isLoading}
      cancelText={t('cancel')}
      confirmText={t('deviceModal.assignConfirm')}
    >
      <Box className='flex flex-col gap-2 w-full'>
        <InputLabel
          shrink={false}
          className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
        >
          {t('deviceModal.deviceInSystemLabel')}
        </InputLabel>
        <BaseSelect<IMasterDataItem>
          name='imei'
          value={imei}
          onChange={handleChangeImei}
          options={selectOptions}
          placeholder={t('deviceModal.selectDevicePlaceholder')}
          isSearchEnable
          isDisplayAllOption={false}
          fullWidth
          {...(isFetching
            ? { IconComponent: () => <CircularProgress size={18} className='mr-3' aria-label='loading' /> }
            : {})}
        />
      </Box>
    </BaseDeviceModal>
  );
};

export default memo(AssignDeviceModal);
